import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Activity, ArrowUpRight } from 'lucide-react';

const indicators = [
  {
    team: 'USA',
    flag: '🇺🇸',
    momentumScore: 78,
    competitivePressure: 'Low',
    pressureColor: 'text-stable',
    trendVolatility: 'Low',
    volatilityLevel: 22,
    assessment: 'Strong momentum with stable competitive position',
  },
  {
    team: 'Mexico',
    flag: '🇲🇽',
    momentumScore: 54,
    competitivePressure: 'Medium',
    pressureColor: 'text-warning',
    trendVolatility: 'Medium',
    volatilityLevel: 45,
    assessment: 'Stagnating momentum; rising pressure from Canada',
  },
  {
    team: 'Canada',
    flag: '🇨🇦',
    momentumScore: 85,
    competitivePressure: 'Low',
    pressureColor: 'text-stable',
    trendVolatility: 'High',
    volatilityLevel: 68,
    assessment: 'High momentum but volatile; sustainability uncertain',
  },
];

// Progress Bar Component
const ProgressBar = ({ value, type }) => {
  let colorClass;
  if (type === 'momentum') {
    colorClass = value >= 70 ? 'bg-stable' : value >= 50 ? 'bg-warning' : 'bg-destructive';
  } else {
    colorClass = value >= 60 ? 'bg-destructive' : value >= 40 ? 'bg-warning' : 'bg-stable';
  }
  
  return (
    <div className="h-2 bg-muted/30 rounded-full overflow-hidden">
      <div 
        className={`h-full rounded-full transition-all duration-500 ${colorClass}`}
        style={{ width: `${value}%` }}
      />
    </div>
  );
};

// Momentum Card Component
const MomentumCard = ({ ind }) => (
  <Card className="bg-card border-border/50" data-testid={`momentum-card-${ind.team.toLowerCase()}`}>
    <CardHeader className="pb-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{ind.flag}</span>
          <CardTitle className="text-base font-semibold">{ind.team}</CardTitle>
        </div>
      </div>
    </CardHeader>
    <CardContent className="space-y-4">
      {/* Momentum Score */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-muted-foreground uppercase tracking-wider">Momentum Score</span>
          <span className="text-lg font-bold text-foreground">{ind.momentumScore}</span>
        </div>
        <ProgressBar value={ind.momentumScore} type="momentum" />
      </div>
      
      {/* Competitive Pressure */}
      <div className="flex items-center justify-between py-2 border-t border-border/30">
        <span className="text-xs text-muted-foreground uppercase tracking-wider">Competitive Pressure</span>
        <span className={`font-semibold ${ind.pressureColor}`}>{ind.competitivePressure}</span>
      </div>
      
      {/* Trend Volatility */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-muted-foreground uppercase tracking-wider">Trend Volatility</span>
          <span className="text-sm font-medium text-foreground">{ind.trendVolatility}</span>
        </div>
        <ProgressBar value={ind.volatilityLevel} type="volatility" />
      </div>
      
      {/* Assessment */}
      <div className="pt-3 border-t border-border/50">
        <p className="text-xs text-muted-foreground italic">{ind.assessment}</p>
      </div>
    </CardContent>
  </Card>
);

// Executive Summary Box Component
const ExecutiveSummaryBox = () => (
  <Card className="mt-5 bg-muted/20 border-border/50">
    <CardContent className="p-5">
      <div className="flex items-start gap-4">
        <div className="p-2 rounded-lg bg-primary/10">
          <ArrowUpRight className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h4 className="font-semibold text-foreground mb-2">Executive Summary</h4>
          <p className="text-sm text-muted-foreground leading-relaxed">
            USA maintains strong momentum with low competitive pressure, positioning well for 2026. 
            Canada presents the highest momentum but with elevated volatility—watch for sustainability signals. 
            Mexico requires strategic intervention to avoid further gap widening. Overall CONCACAF competitive 
            landscape favors US Soccer Federation through the 2026 cycle.
          </p>
        </div>
      </div>
    </CardContent>
  </Card>
);

// SECTION 4: Momentum Risk Indicator
export const MomentumIndicator = () => {
  return (
    <section className="mb-10" data-testid="momentum-risk-section">
      <div className="flex items-center gap-3 mb-6">
        <Activity className="w-6 h-6 text-warning" />
        <h2 className="text-lg font-semibold text-foreground tracking-tight">Momentum Risk Indicator</h2>
      </div>
      
      <div className="grid md:grid-cols-3 gap-5">
        {indicators.map((ind) => (
          <MomentumCard key={ind.team} ind={ind} />
        ))}
      </div>
      
      <ExecutiveSummaryBox />
    </section>
  );
};

export default MomentumIndicator;
